import {
  MetricTimeUnit,
  FirehoseStatus,
  ReturnStatus,
  MeasureType,
  MessageConst,
  TopicComparisonMeasureType
} from 'enums'
import { getMetricSeries } from 'utils/measureTypeUtil'
import { formatMetricSeriesType } from 'utils/metricSeriesTypeUtil'
import { DashboardWidgetData } from 'features/widget/types'
import { TopicComparisonWidgetType } from 'components/widget/TopicComparisonWidgets/types'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import { MetricSeries } from 'schemas/MetricSeriesSchema'
import { TopicInfo } from 'schemas/TopicInfoSchema'
import { getNumberFormatter, getPercentFormatter } from './format'
import { RowData } from './types'
import getComparator from './sort'

const getTotals = (rowData: RowData[] = []) =>
  rowData.reduce(
    (totals, row) => {
      const netSentiment = row[TopicComparisonMeasureType.NET_SENTIMENT]
      const netSentimentValue =
        netSentiment && netSentiment.formattedValue !== MessageConst.EM_DASH
          ? netSentiment.value
          : 0
      const passion = row[TopicComparisonMeasureType.PASSION]
      const passionValue =
        passion && passion.formattedValue !== MessageConst.EM_DASH ? passion.value : 0
      return {
        totalMentions: totals.totalMentions + row[TopicComparisonMeasureType.MENTIONS]?.value || 0,
        totalPosts: totals.totalPosts + row[TopicComparisonMeasureType.POSTS]?.value || 0,
        totalSentiment: totals.totalSentiment + netSentimentValue,
        totalPassion: totals.totalPassion + passionValue
      }
    },
    {
      totalMentions: 0,
      totalPosts: 0,
      totalSentiment: 0,
      totalPassion: 0
    }
  )

const calcAveragePassion = (total: number, rowData: RowData[]) => {
  const passionData = rowData.map((row) => row[TopicComparisonMeasureType.PASSION])
  const numberFormatter = getNumberFormatter()

  if (passionData.length === 0) return { value: 0, formattedValue: numberFormatter.format(0) }

  // display "—"(em dash) When all the rows are "—"(em dash).
  const validData = passionData.filter((d) => d.formattedValue !== MessageConst.EM_DASH)
  if (validData.length === 0) {
    return { value: undefined, formattedValue: MessageConst.EM_DASH }
  }

  const average = total / validData.length
  return { value: average, formattedValue: numberFormatter.format(average) }
}

const calcAverageSentiment = (total: number, rowData: RowData[]) => {
  const sentimentData = rowData.map((row) => row[TopicComparisonMeasureType.NET_SENTIMENT])
  const percentFormatter = getPercentFormatter({
    maximumFractionDigits: 3,
    showLessThan1Percent: false
  })

  if (sentimentData.length === 0) return { value: 0, formattedValue: percentFormatter.format(0) }

  // display "—"(em dash) When all the rows are "—"(em dash).
  const validData = sentimentData.filter((d) => d.formattedValue !== MessageConst.EM_DASH)
  if (validData.length === 0) {
    return { value: undefined, formattedValue: MessageConst.EM_DASH }
  }

  const average = total / validData.length
  return { value: average, formattedValue: percentFormatter.format(average / 100) }
}

const withStatistics = (rowData: RowData[] = []) => {
  const { totalMentions, totalPosts, totalSentiment, totalPassion } = getTotals(rowData)
  const percentFormatter = getPercentFormatter({ showLessThan1Percent: false })
  const rowDataWithStatistics = rowData.map((item) => {
    const mentions = item[TopicComparisonMeasureType.MENTIONS]?.value || 0
    const posts = item[TopicComparisonMeasureType.POSTS]?.value || 0
    const mentionsShared = mentions / (totalMentions || 1)
    const postsShared = posts / (totalPosts || 1)
    return {
      ...item,
      [TopicComparisonMeasureType.MENTIONS_PERCENT_SHARE]: {
        value: mentionsShared,
        formattedValue: percentFormatter.format(mentionsShared)
      },
      [TopicComparisonMeasureType.POSTS_PERCENT_SHARE]: {
        value: postsShared,
        formattedValue: percentFormatter.format(postsShared)
      }
    }
  })

  const averagePassion = calcAveragePassion(totalPassion, rowData)
  const averageSentiment = calcAverageSentiment(totalSentiment, rowData)
  const adjustedAvgSentiment = averageSentiment.value! / 2 + 50
  const numberFormatter = getNumberFormatter()

  return {
    rowData: rowDataWithStatistics,
    statistics: {
      totalMentions: numberFormatter.format(totalMentions),
      totalPosts: numberFormatter.format(totalPosts),
      averagePassion,
      averageSentiment,
      adjustedAvgSentiment
    }
  }
}

const getTrendDataKey = (dataSet: MetricSeries) => {
  if (dataSet.seriesName === getMetricSeries(MeasureType.MENTIONS)[0]) {
    return TopicComparisonMeasureType.MENTIONS_TREND
  }
  if (dataSet.seriesName === getMetricSeries(MeasureType.POSTS)[0]) {
    return TopicComparisonMeasureType.POSTS_TREND
  }
  return ''
}

const convertToTrendData = (dataSets: MetricSeries[]) =>
  dataSets
    .map((dataSet) => ({ key: getTrendDataKey(dataSet), dataSet }))
    .filter(({ key }) => !!key)
    .reduce((aggregatedObj, { key, dataSet }) => ({ ...aggregatedObj, [key]: dataSet.set }), {})

const getMeasureDataKey = (dataSet: MetricSeries) => {
  switch (dataSet.seriesName) {
    case getMetricSeries(MeasureType.MENTIONS)[0]:
      return TopicComparisonMeasureType.MENTIONS
    case getMetricSeries(MeasureType.POSTS)[0]:
      return TopicComparisonMeasureType.POSTS
    case getMetricSeries(MeasureType.UNIQUE_AUTHORS)[0]:
      return TopicComparisonMeasureType.UNIQUE_AUTHORS
    case getMetricSeries(MeasureType.IMPRESSIONS)[0]:
      return TopicComparisonMeasureType.IMPRESSIONS
    case getMetricSeries(MeasureType.PASSION_INTENSITY)[0]:
      return TopicComparisonMeasureType.PASSION
    case getMetricSeries(MeasureType.NET_SENTIMENT)[0]:
      return TopicComparisonMeasureType.NET_SENTIMENT
    case getMetricSeries(MeasureType.ENGAGEMENTS)[0]:
      return TopicComparisonMeasureType.ENGAGEMENTS
    case getMetricSeries(MeasureType.ENGAGEMENT_RATIO_ONLY)[0]:
      return TopicComparisonMeasureType.ENGAGEMENT_RATIO_ONLY
    case getMetricSeries(MeasureType.COMMENTS)[0]:
      return TopicComparisonMeasureType.COMMENTS
    case getMetricSeries(MeasureType.SHARES)[0]:
      return TopicComparisonMeasureType.SHARES
    case getMetricSeries(MeasureType.LIKES)[0]:
      return TopicComparisonMeasureType.LIKES
    case getMetricSeries(MeasureType.DISLIKES)[0]:
      return TopicComparisonMeasureType.DISLIKES
    case getMetricSeries(MeasureType.VIEWS)[0]:
      return TopicComparisonMeasureType.VIEWS
    case getMetricSeries(MeasureType.SCORE)[0]:
      return TopicComparisonMeasureType.SCORE
    default:
      return ''
  }
}

const convertToMeasureData = (dataSets: MetricSeries[]) =>
  dataSets
    .map((dataSet) => ({ key: getMeasureDataKey(dataSet), dataSet }))
    .filter(({ key }) => !!key)
    .reduce((aggregatedObj, { key, dataSet }) => {
      const [value] = dataSet.set!
      const formattedValue =
        dataSet.status === ReturnStatus.LANGUAGE_NOT_SUPPORTED
          ? MessageConst.EM_DASH
          : formatMetricSeriesType(dataSet.seriesName, value)

      return { ...aggregatedObj, [key]: { value, formattedValue } }
    }, {})

const retrieveDataSet = (response: DashboardWidgetData<AnnotatedMetricResult>) =>
  response.results![0].data.metrics![0].dataset
const retrieveTimeUnit = (response: DashboardWidgetData<AnnotatedMetricResult>) =>
  response.results![0].service.properties.timeUnits![0]
const isTrendData = (timeUnit: MetricTimeUnitStrings) => timeUnit !== MetricTimeUnit.ALL

const convertResponses = (responses: DashboardWidgetData<AnnotatedMetricResult>[]) =>
  responses.reduce((aggregatedObj, response) => {
    const dataSet = retrieveDataSet(response)
    const timeUnit = retrieveTimeUnit(response)
    return {
      ...aggregatedObj,
      ...(isTrendData(timeUnit!) ? convertToTrendData(dataSet!) : convertToMeasureData(dataSet!))
    }
  }, {})

const retrieveTopicId = (response: DashboardWidgetData<AnnotatedMetricResult>) =>
  response.results![0].service.parameters.topicIds![0]

/**
 * convert responses from runsearch to data displayed on table
 * @param searchResponses responses from runsearch
 * @param widgetSetting widget setting
 */
const convertToTableData = (
  searchResponses: DashboardWidgetData<AnnotatedMetricResult>[] | undefined,
  widgetSetting: TopicComparisonWidgetType
) => {
  const { topicInfos, tcRankBy } = widgetSetting
  const rowData = topicInfos
    ?.map((topicInfo: TopicInfo) => {
      const responses = searchResponses?.filter((r) => retrieveTopicId(r) === topicInfo.topicId)
      return {
        contentType: topicInfo.contentType,
        topicName: topicInfo.name,
        topicId: topicInfo.topicId,
        imageUrl: topicInfo.displayImgUrl,
        fhTopic: FirehoseStatus.ON === topicInfo.fhTwitterStatus,
        instantSearch: topicInfo.sampledGIIndex,
        scale: topicInfo.scale,
        ...convertResponses(responses || [])
      }
    })
    .sort(getComparator(tcRankBy!))
  return withStatistics(rowData!)
}

export default convertToTableData
